/**
 * Mock 模块：union_city_war_homepage
 * @see mockMd/union_city_war_homepage.md
 */
import {
  UnionCityWarBuildingRewardStatus,
  UnionCityWarGiftBonus,
  UnionCityWarRole,
  UnionCityWarStage,
  UnionCityWarTier,
  type UnionCityWarBuildingRewardItem,
  type UnionCityWarHomepageData,
  type UnionCityWarHomepagePkInfo,
  type UnionCityWarRewardInfoItem,
  type UnionCityWarRewardItem,
} from '../types'
import storiesArenaGiftIcon from '../components/ArenaDrawer/stories-images/stories-gift-icon.png'
import storiesGiftIcon from '../components/BuildingRewards/stories-images/stories-gift-icon.png'
import storiesWinGiftIcon from '../components/WinRewards/stories-images/stories-gift-icon.png'
import storiesUnionAvatar from '../components/GroupStageRank/stories-images/stories-union-avatar.png'
import storiesPkUnionAvatar from '../components/PkPeriodHero/stories-images/stories-union-avatar.png'

const AVATAR = storiesUnionAvatar
const PK_AVATAR = storiesPkUnionAvatar

/** 建造奖励格文案（稿面双行 gift name · `名称*后缀`） */
export const MOCK_BUILDING_REWARD_TOAST_NAME = 'Gift name*Gift name'

const rewardItem: UnionCityWarRewardItem = {
  id: 10001,
  name: 'Avatar Frame',
  view_icon: AVATAR,
  toast_name: 'Avatar Frame*3d',
  reward_type: 1,
  tag: 'level_A_10',
  quantity: 3,
  reward_value: 100,
  has_show_effect: false,
}

const buildingRewardGift: UnionCityWarRewardItem = {
  id: 20001,
  name: 'Gift name',
  view_icon: storiesGiftIcon,
  toast_name: MOCK_BUILDING_REWARD_TOAST_NAME,
  reward_type: 1,
  tag: 'level_S_10',
  quantity: 1,
  reward_value: 100,
  has_show_effect: true,
}

/** 有奖励城建值时追加在该等级 `rewards` 末尾 @see mockMd/union_city_war_homepage.md */
const cityScoreRewardItem: UnionCityWarRewardItem = {
  id: 30001,
  name: '城建值{0}',
  view_icon: 'https://img01.mehiya.com/imgv2/png/id/173698800235',
  toast_name: '城建值{0}',
  reward_type: 1,
  tag: '',
  quantity: 1,
  reward_value: 0,
  has_show_effect: false,
}

const buildingRewardGifts = [
  buildingRewardGift,
  {...buildingRewardGift, id: 20002, tag: 'level_S_10_b'},
  {...buildingRewardGift, id: 20003, tag: 'level_S_10_c'},
]

/** 对齐 state-leader：5 行 × 3 格 · status 已领/可领/不可领×3 */
const buildingRewardsVrtList: UnionCityWarBuildingRewardItem[] = [
  {
    level: 10,
    status: UnionCityWarBuildingRewardStatus.Claimed,
    rewards: buildingRewardGifts,
  },
  {
    level: 20,
    status: UnionCityWarBuildingRewardStatus.Claimable,
    rewards: buildingRewardGifts,
  },
  {
    level: 30,
    status: UnionCityWarBuildingRewardStatus.Locked,
    rewards: buildingRewardGifts,
  },
  {
    level: 40,
    status: UnionCityWarBuildingRewardStatus.Locked,
    rewards: buildingRewardGifts,
  },
  {
    level: 50,
    status: UnionCityWarBuildingRewardStatus.Locked,
    rewards: buildingRewardGifts,
  },
]

/** 稿面倒计时 59:28:47 → 214127s（VRT 对齐；真实单场 ≤1h） */
const PK_VRT_COUNTDOWN_SEC = 59 * 3600 + 28 * 60 + 47

const createPkInfo = (
  ownName: string,
  opponentName: string,
  ownScore: number,
  opponentScore: number,
  options?: {
    ownLevel?: number
    opponentLevel?: number
    endCountdown?: number
    nextOpenTime?: string
    opponentIsRobot?: boolean
  }
): UnionCityWarHomepagePkInfo => ({
  slot_time: '20:00-21:00',
  end_countdown: options?.endCountdown ?? PK_VRT_COUNTDOWN_SEC,
  next_open_time: options?.nextOpenTime ?? '21:00',
  own: {
    city_level: options?.ownLevel ?? 20,
    union_name: ownName,
    union_avatar: PK_AVATAR,
    city_score: ownScore,
    is_robot: false,
  },
  opponent: {
    city_level: options?.opponentLevel ?? 10,
    union_name: opponentName,
    union_avatar: PK_AVATAR,
    city_score: opponentScore,
    is_robot: options?.opponentIsRobot ?? false,
  },
})

type UnionCityWarHomepageBase = Omit<
  UnionCityWarHomepageData,
  'role' | 'pk_info'
>

const baseHomepage: UnionCityWarHomepageBase = {
  err_msg: '',
  is_first_enter: false,
  is_leader: true,
  union_id: 10086,
  activity_id: 10086,
  activity_time: '08.01-08.13',
  tier: UnionCityWarTier.A,
  stage: UnionCityWarStage.Final,
  city_level: 12,
  city_score: 240000,
  next_level_need: 10000,
  direct_promote_left: 3,
  gift_city_score: 180000,
  task_city_score: 40000,
  pk_city_score: 20000,
  sign_city_score: 100,
  sign_done: true,
  gift_task_done: 10000,
  gift_task_need: 10000,
  gift_reward_city_score: 200,
  normal_gift_bonus: UnionCityWarGiftBonus.NormalDefault,
  special_gift_bonus: UnionCityWarGiftBonus.SpecialDefault,
  pk_win_city_score: 5000,
  pk_threshold: 10000,
  anchor_threshold: 1000,
  pk_start_time: '18:00:00',
  pk_end_time: '23:00:00',
  pk_slot_times: ['18:00-19:00', '20:00-21:00'],
  pk_enable: false,
  pk_box_rewards: [],
  building_rewards: [
    {
      level: 10,
      status: UnionCityWarBuildingRewardStatus.Claimable,
      rewards: [rewardItem, cityScoreRewardItem],
    },
    {
      level: 20,
      status: UnionCityWarBuildingRewardStatus.Claimed,
      rewards: [rewardItem, cityScoreRewardItem],
    },
  ],
  can_remind: false,
  rank_rise_list: [
    {
      union_id: 10086,
      union_name: 'USER NAME US...',
      union_avatar: AVATAR,
    },
    {
      union_id: 10087,
      union_name: 'UNION TWO',
      union_avatar: AVATAR,
    },
    {
      union_id: 10088,
      union_name: 'UNION THREE',
      union_avatar: AVATAR,
    },
  ],
  stage_list: [
    {
      stage: UnionCityWarStage.Group,
      time_range: '04.06-04.12',
      countdown_sec: 5183999,
      promote_count: 10,
      direct_promote_level: 10,
      direct_promote_score: 400000,
    },
    {
      stage: UnionCityWarStage.Promotion,
      time_range: '04.06-04.12',
      countdown_sec: 0,
      promote_count: 5,
      direct_promote_level: 30,
      direct_promote_score: 800000,
    },
    {
      stage: UnionCityWarStage.Final,
      time_range: '04.06-04.12',
      countdown_sec: 0,
      promote_count: 3,
      direct_promote_level: 40,
      direct_promote_score: 1200000,
    },
  ],
}

/** 荣耀竞技场说明半屏 · 稿面 4 格 +10 / 门槛 1000 */
export const MOCK_ARENA_BOX_TOAST_NAME = 'Gift name*Gift name'

const arenaBoxReward = (id: number): UnionCityWarRewardInfoItem => ({
  id,
  activity_id: 123456,
  origin_name: 'Chest Reward',
  view_icon: storiesArenaGiftIcon,
  toast_name: MOCK_ARENA_BOX_TOAST_NAME,
  reward_type: 1,
  reward_string: 'Gift name',
  reward_quantity: 1,
  reward_tag: 'pk_box',
  reward_value: 100,
  has_show_effect: true,
})

/** @see mockMd/union_city_war_homepage.md · 荣耀竞技场说明半屏（默认 VRT） */
export const UnionCityWarHomepageArenaDrawerMock: UnionCityWarHomepageData = {
  ...baseHomepage,
  role: UnionCityWarRole.Anchor,
  is_leader: true,
  building_rewards: buildingRewardsVrtList,
  is_first_enter: true,
  pk_info: [
    createPkInfo('USER NAME US...', 'USER NAME US...', 26500, 26500),
    createPkInfo('Sun Guild', 'Robot Guild', 7000, 6500, {
      opponentIsRobot: true,
      opponentLevel: 11,
    }),
  ],
  pk_box_rewards: [
    arenaBoxReward(40001),
    arenaBoxReward(40002),
    arenaBoxReward(40003),
    arenaBoxReward(40004),
  ],
}

/** 默认 Mock：主播非 PK · 可领取城邦主视觉（本任务 VRT） */
export const UnionCityWarHomepageMock = UnionCityWarHomepageArenaDrawerMock
